import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Space, message, Spin, Select } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import CreditCardService from '../service/credit-card-service/Credit-card.service';
import { CreditCardListQueryParams, CreditCardResponse2 } from '../model/Credit-card.model';
import {
  CreditCardServerFilters,
  defaultCreditCardServerFilters,
} from '../model/credit-card-filters';
import CreditCardTable from '../component/credit-card/CreditCardTable';
import CreateCreditCardModal from '../component/credit-card/CreateCreditCardModal';

const { Option } = Select;

function buildListQuery(
  filters: CreditCardServerFilters,
  page: number,
  size: number
): CreditCardListQueryParams {
  const q: CreditCardListQueryParams = {
    page: page - 1,
    size,
  };
  if (filters.search.trim()) q.search = filters.search.trim();
  if (filters.bankIds.length) q.bankIds = filters.bankIds;
  if (filters.cardTypeIds.length) q.cardTypeIds = filters.cardTypeIds;
  if (filters.customerId !== null) q.customerId = filters.customerId;
  if (filters.statuses.length) q.statuses = filters.statuses;
  if (filters.newStatuses.length) q.newStatuses = filters.newStatuses;
  if (filters.isActive !== null) q.isActive = filters.isActive;
  if (filters.partnerIds.length) q.partnerIds = filters.partnerIds;
  if (filters.scheduleMonthBuckets.length) {
    q.scheduleMonthBuckets = filters.scheduleMonthBuckets;
  }
  if (
    filters.expiryDateRange &&
    filters.expiryDateRange[0] &&
    filters.expiryDateRange[1]
  ) {
    q.expiryFrom = filters.expiryDateRange[0].slice(0, 10);
    q.expiryTo = filters.expiryDateRange[1].slice(0, 10);
  }
  if (
    filters.creditLimitRange &&
    typeof filters.creditLimitRange[0] === 'number' &&
    typeof filters.creditLimitRange[1] === 'number'
  ) {
    q.creditLimitMin = filters.creditLimitRange[0];
    q.creditLimitMax = filters.creditLimitRange[1];
  }
  if (
    filters.defaultFeeRateRange &&
    typeof filters.defaultFeeRateRange[0] === 'number' &&
    typeof filters.defaultFeeRateRange[1] === 'number'
  ) {
    q.defaultFeeRateMin = filters.defaultFeeRateRange[0];
    q.defaultFeeRateMax = filters.defaultFeeRateRange[1];
  }
  if (filters.cyclePaymentDays.length) {
    q.cyclePaymentDays = filters.cyclePaymentDays;
  }
  if (filters.creditLimitContains.trim()) {
    q.creditLimitContains = filters.creditLimitContains.trim();
  }
  if (filters.defaultFeeRateContains.trim()) {
    q.defaultFeeRateContains = filters.defaultFeeRateContains.trim();
  }
  if (filters.expiryDateContains.trim()) {
    q.expiryDateContains = filters.expiryDateContains.trim();
  }
  return q;
}

const CreditCardsPage: React.FC = () => {
  const [creditCards, setCreditCards] = useState<CreditCardResponse2[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [statistics, setStatistics] = useState({
    totalCards: 0,
    completedCards: 0,
    pendingCards: 0,
    noScheduleCards: 0,
  });
  const [filters, setFilters] = useState<CreditCardServerFilters>(() =>
    defaultCreditCardServerFilters()
  );

  const loadCreditCards = useCallback(async () => {
    try {
      setLoading(true);
      const listQuery = buildListQuery(filters, currentPage, pageSize);
      const { page: _p, size: _s, ...filterOnly } = listQuery;
      const [pageData, statsData] = await Promise.all([
        CreditCardService.getCreditCardsWithSchedulesByMonthPaged(
          selectedMonth,
          selectedYear,
          listQuery
        ),
        CreditCardService.getCreditCardMonthStatistics(
          selectedMonth,
          selectedYear,
          filterOnly
        ),
      ]);
      setCreditCards(pageData.content);
      setTotal(pageData.totalElements);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error loading credit cards:', error);
      message.error('Không thể tải danh sách thẻ tín dụng');
    } finally {
      setLoading(false);
    }
  }, [
    selectedMonth,
    selectedYear,
    filters,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    loadCreditCards();
  }, [loadCreditCards]);

  const handleFiltersChange = (newFilters: Partial<CreditCardServerFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  };

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    loadCreditCards();
    message.success('Tạo thẻ tín dụng thành công');
  };

  const handleUpdateSuccess = () => {
    loadCreditCards();
    message.success('Cập nhật thẻ tín dụng thành công');
  };

  const handleDeleteSuccess = () => {
    loadCreditCards();
    message.success('Xóa thẻ tín dụng thành công');
  };

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Card className="shadow-md">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Quản lý thẻ tín dụng
              </h1>
              <p className="text-gray-600">
                Quản lý tất cả thẻ tín dụng trong hệ thống
              </p>
              <div className="mt-3 flex space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Tổng:</span>
                  <span className="font-semibold text-blue-600">
                    {statistics.totalCards}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Hoàn thành:</span>
                  <span className="font-semibold text-green-600">
                    {statistics.completedCards}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Chưa hoàn thành:</span>
                  <span className="font-semibold text-yellow-600">
                    {statistics.pendingCards}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Chưa có lịch:</span>
                  <span className="font-semibold text-gray-600">
                    {statistics.noScheduleCards}
                  </span>
                </div>
              </div>
            </div>
            <Space>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  Tháng:
                </span>
                <Select
                  value={selectedMonth}
                  onChange={(v) => {
                    setSelectedMonth(v);
                    setCurrentPage(1);
                  }}
                  style={{ width: 100 }}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <Option key={month} value={month}>
                      Tháng {month}
                    </Option>
                  ))}
                </Select>
                <span className="text-sm font-medium text-gray-700">Năm:</span>
                <Select
                  value={selectedYear}
                  onChange={(v) => {
                    setSelectedYear(v);
                    setCurrentPage(1);
                  }}
                  style={{ width: 100 }}
                >
                  {Array.from(
                    { length: 5 },
                    (_, i) => dayjs().year() - 2 + i
                  ).map((year) => (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  ))}
                </Select>
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadCreditCards}
                loading={loading}
              >
                Tải lại
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
              >
                Tạo thẻ mới
              </Button>
            </Space>
          </div>
        </div>

        <Spin spinning={loading}>
          <CreditCardTable
            creditCards={creditCards}
            onUpdateSuccess={handleUpdateSuccess}
            onDeleteSuccess={handleDeleteSuccess}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            serverPagination={{
              current: currentPage,
              pageSize,
              total,
              onChange: handlePaginationChange,
            }}
          />
        </Spin>

        <CreateCreditCardModal
          visible={createModalVisible}
          onClose={() => setCreateModalVisible(false)}
          onSuccess={handleCreateSuccess}
        />
      </Card>
    </div>
  );
};

export default CreditCardsPage;
